import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useEffect } from "react";
import type { FeedbackValue } from "@volca/ui/components/ui/feedback-toggle";
import { useApi } from "@/core/api";
import { dayjs } from "@/core/utils/date";
import { RUN_STATUS } from "@/profitability/constants";
import { invalidateReportViews, reportKey } from "@/profitability/query";
import { reportsPath } from "@/profitability/report-query";
import type { ProfitabilityRun } from "@/profitability/types";
import { reportPath } from "@/profitability/utils";
import type {
  FeedbackSummary,
  FindingDetail,
  FindingFilter,
  FindingResolution,
  FindingsPage,
  FindingStatus,
} from "./types";

/** Findings still open: detected, being looked into, or waiting on someone. */
export const openFindingCount = (
  counts: Partial<Record<FindingStatus, number>> | undefined,
) =>
  (counts?.open ?? 0) +
  (counts?.investigating ?? 0) +
  (counts?.action_required ?? 0);

export const findingsKey = (businessId: string) => [
  ...reportKey(businessId),
  "findings",
];

export const useFindings = (
  businessId: string,
  reportId: string,
  status: FindingFilter,
  /**
   * Only some findings: one dataset's, some of its records by id
   * (`record_id`, sent whole), one run's (with that run's figures), or
   * the rules named. `perRule` returns the first of each rule instead of a page.
   */
  filter?: {
    dataset?: string;
    recordIds?: string[];
    runId?: string;
    rules?: string[];
    ruleId?: string;
  },
  /** Keep showing the last list while a new filter loads, not a skeleton. */
  {
    keepPrevious = false,
    enabled = true,
    perRule,
  }: { keepPrevious?: boolean; enabled?: boolean; perRule?: number } = {},
) => {
  const api = useApi();
  // The scope stays the last key: the queue reuses a period's counts from
  // any list of that period, whichever rule it was narrowed to.
  const scope = {
    ...(filter?.ruleId ? { ruleId: filter.ruleId } : {}),
    ...(filter?.dataset ? { dataset: filter.dataset } : {}),
    ...(filter?.recordIds ? { recordIds: filter.recordIds } : {}),
    ...(filter?.runId ? { runId: filter.runId } : {}),
  };
  const rules = filter?.rules?.length ? [...filter.rules].sort() : null;
  return useInfiniteQuery({
    queryKey: [
      ...findingsKey(businessId),
      reportId,
      status,
      rules,
      perRule ?? null,
      Object.keys(scope).length ? scope : null,
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      api
        .get<FindingsPage>(`${reportsPath(businessId, reportId)}/findings`, {
          params: {
            status,
            page: pageParam,
            dataset: filter?.dataset,
            record_ids: filter?.recordIds,
            run_id: filter?.runId,
            rule: rules ?? undefined,
            rule_id: filter?.ruleId,
            per_rule: perRule,
          },
          signal,
        })
        .then((res) => res.data),
    getNextPageParam: (page) => (page.has_more ? page.next_page : null),
    placeholderData: keepPrevious ? keepPreviousData : undefined,
    retry: false,
    enabled: enabled && !!reportId,
  });
};

const REVIEW_POLL_MS = 3_000;
/**
 * Bound each reconciliation attempt, including a newly selected historical run.
 */
const REVIEW_WAIT_MINUTES = 10;

/**
 * Whether a run's findings are written yet. A completed run that reviews its
 * findings (`reviews_findings`; never a draft's sample) writes them in the
 * background, and says when it did (`findings_reviewed_at`). A run from
 * before either field was sent is taken as reviewed.
 */
export const awaitingReview = (
  run:
    | Pick<
        ProfitabilityRun,
        | "status"
        | "reviews_findings"
        | "findings_reviewed_at"
        | "completed_at"
        | "findings_review_requested_at"
        | "review_error"
        | "draft_sample"
      >
    | null
    | undefined,
) =>
  !!run &&
  // A draft's sample never has findings written: nothing to wait for.
  !run.draft_sample &&
  run.status === RUN_STATUS.completed &&
  !run.review_error?.message &&
  (run.reviews_findings === true || !!run.findings_review_requested_at) &&
  run.findings_reviewed_at === null &&
  !!(run.findings_review_requested_at ?? run.completed_at) &&
  dayjs().diff(
    dayjs(run.findings_review_requested_at ?? run.completed_at),
    "minute",
  ) < REVIEW_WAIT_MINUTES;

export interface FindingsReviewState {
  reviewsFindings: boolean;
  reviewing: boolean;
  error?: string | null;
}

export const useFindingsReview = (
  businessId: string,
  run: ProfitabilityRun | null | undefined,
): FindingsReviewState => {
  const api = useApi();
  const client = useQueryClient();
  const waiting = awaitingReview(run);
  const runId = run?.id ?? "";
  const poll = useQuery({
    queryKey: [
      ...reportKey(businessId),
      "findings-review",
      runId,
      run?.review_revision ?? null,
    ],
    queryFn: ({ signal }) =>
      api
        .get<{ run: ProfitabilityRun }>(reportPath(businessId, runId), {
          signal,
        })
        .then((res) => res.data.run),
    enabled: waiting,
    refetchInterval: (query) =>
      query.state.data?.findings_reviewed_at ||
      !awaitingReview(query.state.data ?? run)
        ? false
        : REVIEW_POLL_MS,
    retry: false,
  });
  const reviewedAt = poll.data?.findings_reviewed_at ?? null;
  useEffect(() => {
    if (!waiting || !reviewedAt) return;
    void invalidateReportViews(client, businessId);
  }, [businessId, client, reviewedAt, runId, waiting]);
  const error =
    poll.data?.review_error?.message ?? run?.review_error?.message ?? null;
  const reviewing = waiting && !reviewedAt && !error;
  return {
    /** False for a draft's sample: its flagged rows never become findings. */
    reviewsFindings: run?.reviews_findings !== false && !run?.draft_sample,
    /** The background review is still writing the run's findings. */
    reviewing,
    error,
  };
};

export const useFinding = (businessId: string, findingId: string | null) => {
  const api = useApi();
  return useQuery({
    queryKey: [...findingsKey(businessId), "detail", findingId],
    queryFn: ({ signal }) =>
      api
        .get<{
          finding: FindingDetail;
        }>(`/businesses/${businessId}/profitability_findings/${findingId}`, {
          signal,
        })
        .then((res) => res.data.finding),
    enabled: !!findingId,
    retry: false,
  });
};

const useRefreshReview = (businessId: string) => {
  const client = useQueryClient();
  return () => invalidateReportViews(client, businessId);
};

export const useRetryProcessing = (businessId: string, runId: string) => {
  const api = useApi();
  const refresh = useRefreshReview(businessId);
  return useMutation({
    mutationFn: (phase: "review" | "goal" | "analysis") =>
      api.post(`${reportPath(businessId, runId)}/retry_processing`, { phase }),
    onSuccess: refresh,
  });
};

type FindingChange = {
  status?: FindingStatus;
  resolution?: FindingResolution;
  note?: string;
};

const patchFinding = (
  api: AxiosInstance,
  businessId: string,
  findingId: string,
  { status, resolution, note }: FindingChange,
) =>
  api
    .patch<{
      finding: FindingDetail;
    }>(`/businesses/${businessId}/profitability_findings/${findingId}`, {
      status,
      resolution,
      note,
    })
    .then((res) => res.data.finding);

export const useUpdateFinding = (businessId: string) => {
  const api = useApi();
  const refresh = useRefreshReview(businessId);
  return useMutation({
    mutationFn: ({
      findingId,
      ...change
    }: FindingChange & { findingId: string }) =>
      patchFinding(api, businessId, findingId, change),
    onSuccess: refresh,
  });
};

/**
 * Applies one change to several findings at once and refreshes the report
 * views once. Every finding is tried; it rejects with the first failure.
 */
export const useUpdateFindings = (businessId: string) => {
  const api = useApi();
  const refresh = useRefreshReview(businessId);
  return useMutation({
    mutationFn: async ({
      findingIds,
      ...change
    }: FindingChange & { findingIds: string[] }) => {
      const results = await Promise.allSettled(
        findingIds.map((id) => patchFinding(api, businessId, id, change)),
      );
      if (results.some((result) => result.status === "fulfilled"))
        await refresh();
      const failure = results.find((result) => result.status === "rejected");
      if (failure) throw failure.reason;
    },
  });
};

export const useAddFindingNote = (businessId: string) => {
  const api = useApi();
  const refresh = useRefreshReview(businessId);
  return useMutation({
    mutationFn: ({ findingId, body }: { findingId: string; body: string }) =>
      api
        .post<{
          finding: FindingDetail;
        }>(
          `/businesses/${businessId}/profitability_findings/${findingId}/comments`,
          { body },
        )
        .then((res) => res.data.finding),
    onSuccess: refresh,
  });
};

export const useRate = (businessId: string) => {
  const api = useApi();
  const refresh = useRefreshReview(businessId);
  return useMutation({
    mutationFn: (rating: {
      subjectType: "finding" | "insight";
      subjectId: string;
      /** The run whose figures were on screen, so the rating learns from them. */
      runId?: string | null;
      rating: FeedbackValue;
      reason?: string;
      note?: string;
    }) =>
      api
        .put<{
          feedback: FeedbackSummary;
        }>(`/businesses/${businessId}/profitability_feedback`, {
          subject_type: rating.subjectType,
          subject_id: rating.subjectId,
          run_id: rating.runId ?? undefined,
          rating: rating.rating,
          reason: rating.reason,
          note: rating.note,
        })
        .then((res) => res.data.feedback),
    onSuccess: refresh,
  });
};
